import { Alert, Paper, Stack } from '@mui/material';
import type { ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { formatBoxPieces } from '@tiles-erp/shared';
import { usePagination } from '@tiles-erp/hooks';
import type { MultiGodownStockRow } from '@tiles-erp/shared-types';
import { PageContainer } from '@tiles-erp/ui';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { useSessionBranchId } from '../../lib/session-branch';
import { useCatalogOptions } from '../catalog/api';
import { useBranches } from '../products/branch-prices-api';
import { useProductSizes } from '../products/api';
import { useMultiGodownStock } from './api';

const quantity = (row: MultiGodownStockRow, godownId: string): string => {
  const value = row.quantities[godownId] ?? 0;
  return value > 0 ? formatBoxPieces(value, row.piecesPerBox, row.baseUom === 'PIECE' || row.piecesPerBox === 1) : '';
};

/** Finds products whose current positive balance is split across two or more godowns in one branch. */
export function MultiGodownStockReportPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 100 });
  const [branchId, setBranchId] = useSessionBranchId();
  const [brandId, setBrandId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [sizeMm, setSizeMm] = useState('');
  const branches = useBranches();
  const brands = useCatalogOptions('/brands');
  const categories = useCatalogOptions('/categories', brandId || undefined);
  const sizes = useProductSizes();
  const filters = {
    branchId: branchId || undefined, brandId: brandId || undefined,
    categoryId: categoryId || undefined, sizeMm: sizeMm || undefined,
  };
  const report = useMultiGodownStock(pagination.query, filters, Boolean(branchId));
  const columns = useMemo<ColDef<MultiGodownStockRow>[]>(() => [
    { field: 'sku', headerName: 'SKU', minWidth: 125, pinned: 'left' },
    { field: 'productName', headerName: 'Product', minWidth: 230, pinned: 'left' },
    { field: 'brandName', headerName: 'Brand', minWidth: 140 },
    { field: 'categoryName', headerName: 'Category', minWidth: 160 },
    { field: 'sizeMm', headerName: 'Size', minWidth: 100 },
    { field: 'godownCount', headerName: 'Godowns', minWidth: 100 },
    ...(report.data?.godowns ?? []).map<ColDef<MultiGodownStockRow>>((godown) => ({
      headerName: godown.name, minWidth: 135,
      valueGetter: (p) => p.data ? quantity(p.data, godown.id) : '',
      cellStyle: { fontWeight: 700, textAlign: 'right' },
    })),
    { headerName: 'Total stock', minWidth: 140,
      valueGetter: (p) => p.data ? formatBoxPieces(p.data.totalQtyBoxes, p.data.piecesPerBox,
        p.data.baseUom === 'PIECE' || p.data.piecesPerBox === 1) : '',
      cellStyle: { fontWeight: 800, textAlign: 'right' } },
  ], [report.data?.godowns]);

  return <PageContainer title="Products stocked in multiple godowns"
    subtitle="Shows products with a positive current balance in two or more godowns of the selected branch.">
    <Stack spacing={1.5}>
      {report.error && <Alert severity="error">{report.error.message}</Alert>}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          <SearchableSelect label="Branch" value={branchId}
            options={(branches.data ?? []).map((branch) => ({ value: branch.id, label: branch.name }))}
            onChange={(value) => { setBranchId(value); pagination.setPage(1); }} width={240} />
          <SearchableSelect label="Brand" value={brandId} allLabel="All brands"
            options={(brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name }))}
            onChange={(value) => { setBrandId(value); setCategoryId(''); pagination.setPage(1); }} width={210} />
          <SearchableSelect label="Category" value={categoryId} allLabel="All categories"
            options={(categories.data ?? []).map((category) => ({ value: category.id, label: category.name }))}
            onChange={(value) => { setCategoryId(value); pagination.setPage(1); }} width={230} />
          <SearchableSelect label="Size" value={sizeMm} allLabel="All sizes"
            options={(sizes.data ?? []).map((size) => ({ value: size, label: size }))}
            onChange={(value) => { setSizeMm(value); pagination.setPage(1); }} width={160} />
        </Stack>
      </Paper>
      {!branchId ? <Alert severity="info">Select a branch to view the report.</Alert> :
        <DataTable rows={report.data?.items ?? []} columns={columns} meta={report.data?.meta}
          pagination={pagination} loading={report.isFetching} searchPlaceholder="Search SKU or product…"
          exportTitle="Products stocked in multiple godowns" exportFilename="multiple-godown-stock"
          height={650} />}
    </Stack>
  </PageContainer>;
}
